// @ts-nocheck
/* Store Custom File */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import React, { FC } from "react"; // Valtech Custom Code
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Valtech Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import SelectInline, { SelectInlineStyles } from "@store/Library/Components/Toolkit/SelectInline";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useMemo } from "react";

/* Store Custom Code - Start */
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { buttonResetStyles } from "@store/Themes/common/mixins/button";
import { linkStyles } from "@store/Themes/common/mixins/link";
/* Store Custom Code - End */

const iconSize = pxToRem(18);
const iconOffset = pxToRem(0);
/* Valtech Custom Code - End */

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
});

const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
    // Valtech Custom Code
    selectInline?: SelectInlineStyles;
    // Store Custom Code
    languageLink?: LinkPresentationProps;
    languageCurrentWrapper?: InjectableCss;
    languageCurrentText?: TypographyPresentationProps;
}

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            display: flex;
            align-items: center;
            font-size: ${x => x.theme.design.typography.size.smaller}; /* Store Custom Code */
        `,
    },
    languageIcon: {
        size: 20, // Store Custom Code
        src: Globe,
        color: "neutral.contrast", // Store Custom Code
    },
    /* Store Custom Code - Start */
    languageLink: {
        typographyProps: {
            size: 14,
            css: css`
                font-family: inherit;
            `,
        },
        css: css`
            ${buttonResetStyles()}
            ${linkStyles("neutral.contrast", "neutral.contrast", false, true)}
            padding: ${pxToRem(0, 6)};
            cursor: pointer;
            border-left: 1px solid ${getColor("common.background")};

            &:first-child,
            [class^="IconWrapper"] + & {
                border-left: unset;
            }
        `,
    },
    languageCurrentWrapper: {
        css: css`
            border-left: 1px solid ${getColor("common.background")};

            &:first-child,
            [class^="IconWrapper"] + & {
                border-left: unset;
            }
        `,
    },
    languageCurrentText: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            padding: ${pxToRem(0, 6)};
        `,
    },
    /* Store Custom Code - End */
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
    // Store Custom Code
    onClick?: React.EventHandler<React.MouseEvent>;
}

export const LogoImage = styled.img`
    height: 22px;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({
    languages,
    currentLanguage,
    fields,
    setLanguage,
    // Store Custom Code
    onClick,
}) => {
    if (!languages || languages.length <= 1) {
        return null;
    }

    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const menuId = "languageMenu";

    return (
        <StyledWrapper {...styles.languageWrapper}>
            {fields.showIcon &&
                (currentLanguage?.imageFilePath ? (

                    <LogoImage
                        src={currentLanguage.imageFilePath}
                        alt={currentLanguage.languageCode} // Valtech Custom Code
                        aria-hidden="true" // Valtech Custom Code
                    />
                ) : (
                    <Icon {...styles.languageIcon} />
                ))}
            {/* Store Custom Code - Start */}
            {languages.map(lang =>
                lang.id === currentLanguage?.id ? (
                    <StyledWrapper {...styles.languageCurrentWrapper} key={lang.id}>
                        <Typography {...styles.languageCurrentText}>{lang.cultureCode || lang.description}</Typography>
                    </StyledWrapper>
                ) : (
                    <Link
                        onClick={(event: React.MouseEvent) => {
                            setLanguage({ languageId: lang.id });
                            onClick?.(event);
                        }}
                        key={lang.id}
                        {...styles.languageLink}
                        aria-label={translate("Switch site language to {1}").replace("{1}", lang.description)}
                    >
                        {lang.cultureCode || lang.description}
                    </Link>
                ),
            )}
            {/* Store Custom Code - End */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
